import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import HourlyCell from '../components/today/HourlyCell';
import AdminProductionPreview from '../components/admin/AdminProductionPreview';
import {
  getPreviewConfig,
  getPreviewUnit,
  graphResponseToDashboardState,
} from '../config/adminThresholdPreview';
import { translate, getLocationLabel } from '../i18n';
import { useColorPalette } from '../contexts/ColorPaletteContext';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');
const ADMIN_TOKEN_STORAGE_KEY = 'adminThresholdCronSecret';
const SPLIT_PCT_STORAGE_KEY = 'adminThresholdSplitPct';
const SPLIT_PCT_MIN = 28;
const SPLIT_PCT_MAX = 72;

function readSplitPct() {
  const v = parseFloat(sessionStorage.getItem(SPLIT_PCT_STORAGE_KEY));
  return Number.isFinite(v) ? Math.min(SPLIT_PCT_MAX, Math.max(SPLIT_PCT_MIN, v)) : 50;
}

function PanelSplitDivider({ onDrag }) {
  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      onMouseDown={(e) => {
        e.preventDefault();
        onDrag(e.clientX);
      }}
      sx={{
        width: 6,
        flexShrink: 0,
        cursor: 'col-resize',
        bgcolor: '#b0bec5',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: '#78909c' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
      }}
      title="drag"
    >
      <Box sx={{ width: 2, height: 32, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.7)' }} />
    </Box>
  );
}

const TF_DENSE = {
  '& .MuiOutlinedInput-root': { height: 30 },
  '& .MuiOutlinedInput-input': {
    py: 0.25,
    px: 0.75,
    fontSize: 13,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
};

async function api(path, { method = 'GET', token, params } = {}) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${API_BASE}${normalizedPath}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    });
  }
  const res = await fetch(url.toString(), {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const json = await res.json().catch(() => ({}));
  console.log('[AdminDashboard][api]', {
    method,
    path: normalizedPath,
    url: url.toString(),
    status: res.status,
    ok: res.ok,
    params: params || null,
  });
  if (!res.ok) throw new Error(json?.detail || json?.message || `HTTP ${res.status}`);
  return json;
}

async function apiPost(path, body, { token } = {}) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const res = await fetch(`${API_BASE}${normalizedPath}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.detail || json?.message || `HTTP ${res.status}`);
  return json;
}

function toDateInputValue(d) {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function isPastDate(dateStr) {
  if (!dateStr) return false;
  const today = toDateInputValue(new Date());
  return dateStr < today;
}

function readAdminQueryPreviewDate() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const year = params.get('year');
  const month = params.get('month');
  if (!year || !month) return null;
  const y = parseInt(year, 10);
  const m = parseInt(month, 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12 || y < 2020 || y > 2030) return null;
  return `${y}-${String(m).padStart(2, '0')}-01`;
}

function readAdminQueryYearMonth() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const year = params.get('year');
  const month = params.get('month');
  if (!year || !month) return null;
  const y = parseInt(year, 10);
  const m = parseInt(month, 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12 || y < 2020 || y > 2030) return null;
  return `${y}-${String(m).padStart(2, '0')}`;
}

function toNumberOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function calculateScaledLevel(count, minThreshold, maxThreshold, totalLevels = 20) {
  if (count === null || count === undefined || count <= 0) return null;
  if (minThreshold === null || maxThreshold === null) return null;
  const min = Number(minThreshold);
  const max = Number(maxThreshold);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  if (max <= min) return null;
  if (count < min) return 1;
  if (count >= max) return totalLevels;
  const span = max - min;
  const step = span / (totalLevels - 1);
  if (step <= 0) return totalLevels;
  const level = Math.floor((count - min) / step) + 1;
  return Math.min(Math.max(level, 1), totalLevels);
}

const HOUR_START = 7;
const HOUR_END = 22;
const ACTION_GROUP_OPTIONS = [
  {
    value: 'prediction',
    labelJa: '予測',
    labelEn: 'Prediction',
    minLabelJa: '最小人数',
    maxLabelJa: '最大人数',
    midLabelJa: '中央（任意）',
    minLabelEn: 'Min people',
    maxLabelEn: 'Max people',
    midLabelEn: 'Mid (optional)',
    unitNoteJa: '1時間あたりの歩行者数',
    unitNoteEn: 'Pedestrians per hour',
  },
  {
    value: 'calendar',
    labelJa: 'カレンダー',
    labelEn: 'Calendar',
    minLabelJa: '最小人数（1日合計）',
    maxLabelJa: '最大人数（1日合計）',
    midLabelJa: '中央（任意・1日合計）',
    minLabelEn: 'Min people (daily total)',
    maxLabelEn: 'Max people (daily total)',
    midLabelEn: 'Mid (optional, daily total)',
    unitNoteJa: '1日の合計歩行者数',
    unitNoteEn: 'Total pedestrians per day',
  },
  {
    value: 'heatmap_week_time',
    labelJa: 'ヒートマップ(曜日×時間)',
    labelEn: 'Heatmap (Week x Time)',
    minLabelJa: '最小人数',
    maxLabelJa: '最大人数',
    midLabelJa: '中央（任意）',
    minLabelEn: 'Min people',
    maxLabelEn: 'Max people',
    midLabelEn: 'Mid (optional)',
    unitNoteJa: '1時間あたりの歩行者数',
    unitNoteEn: 'Pedestrians per hour',
  },
  {
    value: 'heatmap_date_time',
    labelJa: 'ヒートマップ(日付×時間)',
    labelEn: 'Heatmap (Date x Time)',
    minLabelJa: '最小人数',
    maxLabelJa: '最大人数',
    midLabelJa: '中央（任意）',
    minLabelEn: 'Min people',
    maxLabelEn: 'Max people',
    midLabelEn: 'Mid (optional)',
    unitNoteJa: '1時間あたりの歩行者数',
    unitNoteEn: 'Pedestrians per hour',
  },
  {
    value: 'trend_year',
    labelJa: '年次トレンド',
    labelEn: 'Year Trend',
    minLabelJa: '最小人数（1年合計）',
    maxLabelJa: '最大人数（1年合計）',
    midLabelJa: '中央（任意・1年合計）',
    minLabelEn: 'Min people (yearly total)',
    maxLabelEn: 'Max people (yearly total)',
    midLabelEn: 'Mid (optional, yearly total)',
    unitNoteJa: '1年の合計歩行者数',
    unitNoteEn: 'Total pedestrians per year',
  },
  {
    value: 'trend_month',
    labelJa: '月次トレンド',
    labelEn: 'Month Trend',
    minLabelJa: '最小人数（1か月合計）',
    maxLabelJa: '最大人数（1か月合計）',
    midLabelJa: '中央（任意・1か月合計）',
    minLabelEn: 'Min people (monthly total)',
    maxLabelEn: 'Max people (monthly total)',
    midLabelEn: 'Mid (optional, monthly total)',
    unitNoteJa: '1か月の合計歩行者数',
    unitNoteEn: 'Total pedestrians per month',
  },
  {
    value: 'trend_week',
    labelJa: '週次トレンド',
    labelEn: 'Week Trend',
    minLabelJa: '最小人数（1週間合計）',
    maxLabelJa: '最大人数（1週間合計）',
    midLabelJa: '中央（任意・1週間合計）',
    minLabelEn: 'Min people (weekly total)',
    maxLabelEn: 'Max people (weekly total)',
    midLabelEn: 'Mid (optional, weekly total)',
    unitNoteJa: '1週間の合計歩行者数',
    unitNoteEn: 'Total pedestrians per week',
  },
  {
    value: 'event_effect_hourly',
    labelJa: 'イベント効果(時間)',
    labelEn: 'Event Effect (Hourly)',
    minLabelJa: '最小人数',
    maxLabelJa: '最大人数',
    midLabelJa: '中央（任意）',
    minLabelEn: 'Min people',
    maxLabelEn: 'Max people',
    midLabelEn: 'Mid (optional)',
    unitNoteJa: '1時間あたりの歩行者数',
    unitNoteEn: 'Pedestrians per hour',
  },
  {
    value: 'default',
    labelJa: '共通(フォールバック)',
    labelEn: 'Default (Fallback)',
    minLabelJa: '最小人数（1日合計）',
    maxLabelJa: '最大人数（1日合計）',
    midLabelJa: '中央（任意・1日合計）',
    minLabelEn: 'Min people (daily total)',
    maxLabelEn: 'Max people (daily total)',
    midLabelEn: 'Mid (optional, daily total)',
    unitNoteJa: '1日の合計歩行者数',
    unitNoteEn: 'Total pedestrians per day',
  },
];

function mapActionGroupCatalogEntry(g, adminUiLang) {
  const isEn = adminUiLang === 'en';
  const fb = ACTION_GROUP_OPTIONS.find((o) => o.value === g.id);
  return {
    value: g.id,
    label: isEn ? g.label_en || g.id : g.label_ja || g.id,
    description: isEn ? g.description_en : g.description_ja,
    minLabel: isEn
      ? g.min_label_en || fb?.minLabelEn || 'Min people'
      : g.min_label_ja || fb?.minLabelJa || '最小人数',
    maxLabel: isEn
      ? g.max_label_en || fb?.maxLabelEn || 'Max people'
      : g.max_label_ja || fb?.maxLabelJa || '最大人数',
    midLabel: isEn
      ? g.mid_label_en || fb?.midLabelEn || 'Mid (optional)'
      : g.mid_label_ja || fb?.midLabelJa || '中央（任意）',
    unitNote: isEn ? g.unit_note_en || fb?.unitNoteEn || '' : g.unit_note_ja || fb?.unitNoteJa || '',
  };
}

function buildHourCells(hourly, congestionGetter) {
  const byHour = Object.fromEntries((hourly || []).map((r) => [r.hour, r]));
  const cells = [];
  for (let h = HOUR_START; h <= HOUR_END; h++) {
    const row = byHour[h];
    const raw = row ? congestionGetter(row) : 0;
    const congestion = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
    cells.push({ hour: h, congestion });
  }
  return cells;
}

function buildActualHourCells(actualHourly, minThreshold, maxThreshold) {
  const byHour = Object.fromEntries((actualHourly || []).map((r) => [r.hour, r]));
  const cells = [];
  for (let h = HOUR_START; h <= HOUR_END; h++) {
    const row = byHour[h];
    const level = row ? calculateScaledLevel(row.actual_count ?? null, minThreshold, maxThreshold) : null;
    cells.push({ hour: h, congestion: level ?? 0 });
  }
  return cells;
}

function placeTooltip(loc) {
  return `${getLocationLabel(loc, 'ja')} / ${getLocationLabel(loc, 'en')} — ${loc}`;
}

function placePrimary(loc, uiLang) {
  return getLocationLabel(loc, uiLang === 'en' ? 'en' : 'ja');
}

function formatRange(min, max) {
  if (min == null || max == null) return '—';
  return `${min}–${max}`;
}

function boundsDiffer(a, b) {
  if (!a || !b) return false;
  if (a.draftMin == null || a.draftMax == null || b.draftMin == null || b.draftMax == null) return false;
  return Number(a.draftMin) !== Number(b.draftMin) || Number(a.draftMax) !== Number(b.draftMax);
}

/** 月次予測から選択日の時間別を取得（データが無い日は当月で最初に見つかった日にフォールバック） */
function resolveMonthHourly(predictions, yearMonth, monthDay) {
  if (!Array.isArray(predictions) || !predictions.length) return { hourly: [], usedDate: null };
  const day = String(monthDay).padStart(2, '0');
  const targetDate = `${yearMonth}-${day}`;
  let hourly = predictions.filter((p) => p.date === targetDate);
  if (hourly.length) return { hourly, usedDate: targetDate };
  const dates = [...new Set(predictions.map((p) => p.date).filter((d) => String(d).startsWith(yearMonth)))].sort();
  if (!dates.length) return { hourly: [], usedDate: null };
  const fallbackDate = dates[0];
  return {
    hourly: predictions.filter((p) => p.date === fallbackDate),
    usedDate: fallbackDate,
  };
}

function ThresholdMatrixPanel({
  matrixData,
  selectedLocation,
  selectedGroup,
  onSelectLocation,
  onHighlightCell,
  onEditCell,
  adminUiLang,
  showPlaceSlug,
  t,
}) {
  if (!matrixData?.rows?.length) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
        {t('matrixEmpty')}
      </Typography>
    );
  }
  const groups = matrixData.action_groups || [];
  return (
    <Box sx={{ maxHeight: '42vh', overflow: 'auto' }}>
      {matrixData.rows.map((row) => {
        const loc = row.location;
        const primary = placePrimary(loc, adminUiLang);
        const expanded = selectedLocation === loc;
        return (
          <Accordion
            key={loc}
            expanded={expanded}
            onChange={(_, isExp) => {
              if (isExp) onSelectLocation(loc);
            }}
            disableGutters
            sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid #e0e0e0' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 32, py: 0, '& .MuiAccordionSummary-content': { my: 0.25 } }}>
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11 }}>
                {primary}
                {showPlaceSlug && (
                  <Typography component="span" sx={{ ml: 0.5, fontFamily: 'monospace', fontSize: 9, color: 'text.secondary' }}>
                    {loc}
                  </Typography>
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 0.5, px: 0.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 10, py: 0.25 }}>{t('graphTypeCol')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 10, py: 0.25 }}>{t('thresholdRangeCol')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 10, py: 0.25, width: 40 }} />
                    <TableCell sx={{ fontWeight: 700, fontSize: 10, py: 0.25, width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((g) => {
                    const cell = row.cells?.[g.id] || {};
                    const direct = cell.rule;
                    const builtin = cell.builtin_default;
                    const display = direct || builtin;
                    const label = display ? formatRange(display.min_threshold, display.max_threshold) : '—';
                    const isSelected = selectedLocation === loc && selectedGroup === g.id;
                    return (
                      <TableRow
                        key={g.id}
                        selected={isSelected}
                        hover
                        sx={{ bgcolor: isSelected ? '#e3f2fd' : direct ? '#f1f8e9' : undefined, cursor: 'pointer' }}
                        onClick={() => onHighlightCell(loc, g.id)}
                      >
                        <TableCell sx={{ fontSize: 10, py: 0.2 }}>
                          <Typography component="span" sx={{ display: 'block', fontSize: 10, fontWeight: 600 }}>
                            {adminUiLang === 'en' ? g.label_en : g.label_ja}
                          </Typography>
                          {(adminUiLang === 'en' ? g.unit_note_en : g.unit_note_ja) && (
                            <Typography component="span" sx={{ display: 'block', fontSize: 9, color: 'text.secondary', lineHeight: 1.2 }}>
                              {adminUiLang === 'en' ? g.unit_note_en : g.unit_note_ja}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 10, py: 0.2, fontWeight: direct ? 700 : 400 }}>
                          {label}
                          {!direct && builtin && (
                            <Typography component="span" sx={{ ml: 0.5, fontSize: 9, color: 'text.secondary' }}>
                              ({t('builtinShort')})
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.2 }}>
                          {direct ? (
                            <Chip label="DB" size="small" color="success" sx={{ height: 18, fontSize: 9 }} />
                          ) : (
                            <Chip label="—" size="small" variant="outlined" sx={{ height: 18, fontSize: 9 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.2 }} onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="small"
                            variant={isSelected ? 'contained' : 'outlined'}
                            onClick={() => onEditCell(loc, g.id)}
                            sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: 9, lineHeight: 1.6 }}
                          >
                            {t('matrixEditBtn')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

/** TimeHeatmap / 日历 と同色規則の時間軸1行（管理画面プレビュー用） */
function HeatmapStyleHourRow({ title, hoursData }) {
  const { getCellColor, getTextColor } = useColorPalette();
  return (
    <Box sx={{ mb: 0.75 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25, lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px', bgcolor: '#fff' }}>
        {hoursData.map((h, i) => {
          const gray = h.congestion === 0;
          const bg = gray ? '#e0e0e0' : getCellColor(h.congestion);
          const fg = gray ? '#666' : getTextColor(h.congestion);
          return (
            <Box
              key={h.hour}
              sx={{
                flexShrink: 0,
                width: 36,
                minHeight: 38,
                bgcolor: bg,
                color: fg,
                borderRight: i < hoursData.length - 1 ? '1px solid #ccc' : 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
              }}
            >
              <Typography component="span" sx={{ fontSize: 9, lineHeight: 1 }}>
                {h.hour}
              </Typography>
              <Typography component="span" sx={{ fontSize: 12, lineHeight: 1 }}>
                {gray ? '–' : h.congestion}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function HourlyStripTable({ title, hoursData, showPredictionBadge, isEnglish }) {
  return (
    <Box sx={{ mb: 0.75 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25, lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Box sx={{ border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden', bgcolor: '#fff' }}>
        <Box sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: 6 } }}>
          <Box sx={{ display: 'flex', bgcolor: '#f5f5f5', borderBottom: '1px solid #ddd', width: 'fit-content', minWidth: '100%' }}>
            {hoursData.map((h, index) => (
              <Box
                key={`h-${h.hour}`}
                sx={{
                  minWidth: 42,
                  width: 42,
                  textAlign: 'center',
                  py: 0.25,
                  borderRight: index !== hoursData.length - 1 ? '1px solid #ddd' : 'none',
                  flexShrink: 0,
                }}
              >
                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: 11 }}>
                  {isEnglish ? h.hour : `${h.hour}時`}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', width: 'fit-content', minWidth: '100%' }}>
            {hoursData.map((h, index) => (
              <HourlyCell
                key={`c-${h.hour}`}
                hourData={h}
                index={index}
                totalLength={hoursData.length}
                isGrayCell={h.congestion === 0}
                showPredictionBadge={Boolean(showPredictionBadge && h.congestion > 0)}
                isEnglish={isEnglish}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function ExpandedLinePreview({ hourly, productionMin, productionMax, draftMin, draftMax, showCompare, t }) {
  const data = useMemo(() => {
    if (!hourly?.length) return [];
    if (showCompare) {
      return hourly.map((row) => ({
        hour: row.hour,
        before: row.congestion_level ?? null,
        after: calculateScaledLevel(row.predicted_count ?? null, draftMin, draftMax),
      }));
    }
    return hourly.map((row) => ({
      hour: row.hour,
      level: row.congestion_level ?? calculateScaledLevel(row.predicted_count ?? null, productionMin, productionMax),
    }));
  }, [hourly, showCompare, draftMin, draftMax, productionMin, productionMax]);

  if (!data.length) return null;

  return (
    <Box sx={{ width: '100%', minWidth: 0, mb: 0.75 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
        {t('expandedLineTitle')}
      </Typography>
      <Box sx={{ width: '100%', height: 212, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={208} debounce={40}>
          <LineChart data={data} margin={{ top: 4, right: 12, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 2" stroke="#eee" />
            <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
            <YAxis domain={[0, 20]} width={28} tick={{ fontSize: 10 }} />
            <RechartsTooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {showCompare ? (
              <>
                <Line type="monotone" dataKey="before" name={t('chartBeforeEdit')} stroke="#1565c0" strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="after" name={t('chartAfterEdit')} stroke="#8e24aa" strokeWidth={2} dot={false} connectNulls />
              </>
            ) : (
              <Line type="monotone" dataKey="level" name={t('chartCurrent')} stroke="#1565c0" strokeWidth={2} dot={false} connectNulls />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

function DashboardStylePreview({
  locKey,
  previewData,
  productionMin,
  productionMax,
  draftMin,
  draftMax,
  showThresholdCompare,
  t,
  uiLang,
  layoutMode,
  showSlug,
  preferActualForApi,
}) {
  const hourly = previewData?.hourly || [];
  const actualHourly = previewData?.actualHourly || [];
  const isEnglish = uiLang === 'en';
  const actualByHour = useMemo(() => Object.fromEntries(actualHourly.map((r) => [r.hour, r])), [actualHourly]);
  const apiHours = useMemo(
    () =>
      buildHourCells(hourly, (r) => {
        if (preferActualForApi) {
          const actual = actualByHour[r.hour];
          const lv = calculateScaledLevel(actual?.actual_count ?? null, productionMin, productionMax);
          return lv != null ? lv : 0;
        }
        return r.congestion_level != null ? Number(r.congestion_level) : 0;
      }),
    [hourly, preferActualForApi, actualByHour, productionMin, productionMax]
  );
  const afterHours = useMemo(
    () =>
      buildHourCells(hourly, (r) => {
        const lv = calculateScaledLevel(r.predicted_count ?? null, draftMin, draftMax);
        return lv != null ? lv : 0;
      }),
    [hourly, draftMin, draftMax]
  );
  const actualHours = useMemo(
    () => buildActualHourCells(actualHourly, productionMin, productionMax),
    [actualHourly, productionMin, productionMax]
  );
  const displayName = placePrimary(locKey, uiLang);
  const thresholdRangeLabel = formatRange(
    showThresholdCompare ? draftMin : productionMin,
    showThresholdCompare ? draftMax : productionMax
  );

  return (
    <Box sx={{ pt: 0.25 }}>
      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.35 }}>
        {t('previewTitle')} — {displayName}
        {showSlug && (
          <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontWeight: 400 }}>
            {' '}
            ({locKey})
          </Typography>
        )}
        {thresholdRangeLabel !== '—' && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontWeight: 400 }}>
            {t('previewThresholdRange', { range: thresholdRangeLabel })}
          </Typography>
        )}
      </Typography>

      {layoutMode === 'focus' && (
        <ExpandedLinePreview
          hourly={hourly}
          productionMin={productionMin}
          productionMax={productionMax}
          draftMin={draftMin}
          draftMax={draftMax}
          showCompare={showThresholdCompare}
          t={t}
        />
      )}

      {showThresholdCompare && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: 10 }}>
          {t('compareHintShort', { before: formatRange(productionMin, productionMax), after: formatRange(draftMin, draftMax) })}
        </Typography>
      )}

      {showThresholdCompare ? (
        <>
          <HeatmapStyleHourRow title={t('stripBeforeEdit')} hoursData={apiHours} />
          <HeatmapStyleHourRow title={t('stripAfterEdit')} hoursData={afterHours} />
          <HourlyStripTable title={t('stripBeforeEdit')} hoursData={apiHours} showPredictionBadge={false} isEnglish={isEnglish} />
          <HourlyStripTable title={t('stripAfterEdit')} hoursData={afterHours} showPredictionBadge isEnglish={isEnglish} />
        </>
      ) : (
        <>
          <HeatmapStyleHourRow title={t('stripCurrent')} hoursData={apiHours} />
          <HourlyStripTable title={t('stripCurrent')} hoursData={apiHours} showPredictionBadge={false} isEnglish={isEnglish} />
        </>
      )}
      {actualHourly.length > 0 && (
        <HourlyStripTable
          title={uiLang === 'en' ? 'Actual (scaled)' : '実測（閾値換算）'}
          hoursData={actualHours}
          showPredictionBadge={false}
          isEnglish={isEnglish}
        />
      )}
    </Box>
  );
}

function MiniLevelPreview({
  locKey,
  primaryLabel,
  showSlug,
  hourly,
  actualHourly = [],
  preferActualForApi = false,
  productionMin,
  productionMax,
  draftMin,
  draftMax,
  showThresholdCompare = false,
  previewFetched = true,
  t,
}) {
  const actualByHour = useMemo(() => Object.fromEntries(actualHourly.map((r) => [r.hour, r])), [actualHourly]);
  const data = useMemo(() => {
    if (!hourly?.length) return [];
    if (showThresholdCompare) {
      return hourly.map((row) => ({
        hour: row.hour,
        before: preferActualForApi
          ? calculateScaledLevel(actualByHour[row.hour]?.actual_count ?? null, productionMin, productionMax)
          : row.congestion_level ?? null,
        after: calculateScaledLevel(row.predicted_count ?? null, draftMin, draftMax),
      }));
    }
    return hourly.map((row) => ({
      hour: row.hour,
      level: preferActualForApi
        ? calculateScaledLevel(actualByHour[row.hour]?.actual_count ?? null, productionMin, productionMax)
        : row.congestion_level ?? null,
    }));
  }, [hourly, preferActualForApi, actualByHour, productionMin, productionMax, draftMin, draftMax, showThresholdCompare]);

  if (!data.length) {
    return (
      <Box sx={{ border: '1px solid #ccc', p: 0.25, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, textAlign: 'center', px: 0.25 }}>
          {primaryLabel}
          {showSlug && (
            <>
              <br />
              <span style={{ fontFamily: 'monospace' }}>{locKey}</span>
            </>
          )}
          <br />
          {previewFetched ? t('noPrediction') : t('previewNotFetched')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ border: '1px solid #ccc', p: 0.25, minWidth: 0, bgcolor: '#fff' }}>
      <Typography variant="caption" sx={{ display: 'block', px: 0.25, fontSize: 10, lineHeight: 1.2 }} noWrap title={placeTooltip(locKey)}>
        {primaryLabel}
      </Typography>
      {showSlug && (
        <Typography variant="caption" sx={{ display: 'block', px: 0.25, fontFamily: 'monospace', fontSize: 9, color: 'text.secondary' }} noWrap>
          {locKey}
        </Typography>
      )}
      <Box sx={{ width: '100%', height: 56, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={56} debounce={48}>
          <LineChart data={data} margin={{ top: 0, right: 2, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 2" stroke="#eee" />
            <XAxis dataKey="hour" tick={{ fontSize: 8 }} interval={4} height={14} />
            <YAxis domain={[0, 20]} width={18} tick={{ fontSize: 8 }} />
            <RechartsTooltip />
            {showThresholdCompare ? (
              <>
                <Line type="monotone" dataKey="before" name={t('chartBeforeEdit')} stroke="#1565c0" strokeWidth={1} dot={false} connectNulls />
                <Line type="monotone" dataKey="after" name={t('chartAfterEdit')} stroke="#8e24aa" strokeWidth={1} dot={false} connectNulls />
              </>
            ) : (
              <Line type="monotone" dataKey="level" name={t('chartCurrent')} stroke="#1565c0" strokeWidth={1} dot={false} connectNulls />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

function MonthCalendarPreview({ predictions, yearMonth, selectedDay, onSelectDay, title }) {
  const { getCellColor, getTextColor } = useColorPalette();
  const [year, month] = yearMonth.split('-').map((v) => parseInt(v, 10));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !predictions?.length) return null;
  const dayPeak = {};
  predictions.forEach((p) => {
    const d = Number((p.date || '').split('-')[2]);
    if (!Number.isFinite(d)) return;
    const lv = Number(p.congestion_level || 0);
    dayPeak[d] = Math.max(dayPeak[d] || 0, lv);
  });
  const firstDow = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = Array.from({ length: firstDow }, () => null).concat(
    Array.from({ length: daysInMonth }, (_, i) => i + 1)
  );
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <Box sx={{ mb: 0.75 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.35 }}>{title}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.35 }}>
        {cells.map((d, idx) => {
          if (!d) return <Box key={`empty-${idx}`} sx={{ height: 34 }} />;
          const selected = selectedDay === String(d).padStart(2, '0');
          const level = dayPeak[d] || 0;
          const gray = level === 0;
          const bg = gray ? '#eceff1' : getCellColor(level);
          const fg = gray ? '#666' : getTextColor(level);
          return (
            <Button
              key={d}
              size="small"
              onClick={() => onSelectDay(String(d).padStart(2, '0'))}
              sx={{
                minWidth: 0,
                px: 0,
                py: 0.25,
                fontSize: 10,
                bgcolor: bg,
                color: fg,
                border: selected ? '2px solid #1565c0' : '1px solid #ccc',
                boxShadow: selected ? '0 0 0 1px #1565c0' : 'none',
                fontWeight: selected ? 700 : 500,
                '&:hover': { bgcolor: gray ? '#e0e0e0' : bg, filter: gray ? 'none' : 'brightness(0.95)' },
              }}
            >
              {d}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

export default function AdminDashboard() {
  const queryPreview = readAdminQueryPreviewDate();
  const queryYearMonth = readAdminQueryYearMonth();
  const [adminUiLang, setAdminUiLang] = useState('ja');
  const [showPlaceSlug, setShowPlaceSlug] = useState(false);
  const [previewLayout, setPreviewLayout] = useState('overview');
  const [token, setToken] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [authOpen, setAuthOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [rows, setRows] = useState({});
  const [previewDate, setPreviewDate] = useState(queryPreview || toDateInputValue(new Date()));
  const [actionGroup, setActionGroup] = useState('prediction');
  const previewUnit = useMemo(() => getPreviewUnit(actionGroup), [actionGroup]);
  const previewConfig = useMemo(() => getPreviewConfig(actionGroup), [actionGroup]);
  const isPredictionPreview = previewConfig.kind === 'prediction';
  const [thresholdGroups, setThresholdGroups] = useState([]);
  const [seedingDefaults, setSeedingDefaults] = useState(false);
  const [thresholdView, setThresholdView] = useState('matrix');
  const [matrixData, setMatrixData] = useState(null);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [previewMonth, setPreviewMonth] = useState(queryYearMonth || toDateInputValue(new Date()).slice(0, 7));
  const [previewMonthDay, setPreviewMonthDay] = useState('01');
  const [dayPreviewByLoc, setDayPreviewByLoc] = useState({});
  const [monthPredictionsByLoc, setMonthPredictionsByLoc] = useState({});
  const [productionPreviewByLoc, setProductionPreviewByLoc] = useState({});
  const [previewMeta, setPreviewMeta] = useState(null);
  const loadAllPreviewsRef = useRef(null);
  const [previewStripLoc, setPreviewStripLoc] = useState('');
  const [loadingThresholds, setLoadingThresholds] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [batchNote, setBatchNote] = useState('');
  const [lastBatchSummary, setLastBatchSummary] = useState(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);
  /** 予測閾値を保存した地点（予測バッチのデフォルト対象） */
  const [predictionRecalcQueue, setPredictionRecalcQueue] = useState([]);
  const [historyLoc, setHistoryLoc] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const useActualForPastDay = useMemo(
    () => previewUnit === 'day' && isPastDate(previewDate),
    [previewUnit, previewDate]
  );
  const batchPollTimerRef = useRef(null);
  const splitContainerRef = useRef(null);
  const splitDraggingRef = useRef(false);
  const [splitPct, setSplitPct] = useState(readSplitPct);
  const stopBatchPolling = useCallback(() => {
    if (batchPollTimerRef.current) {
      clearInterval(batchPollTimerRef.current);
      batchPollTimerRef.current = null;
    }
  }, []);

  const fetchBatchProgress = useCallback(
    async (batchId) => {
      try {
        const res = await api('/api/admin/prediction/progress', {
          token,
          params: { batch_id: batchId },
        });
        const p = res?.data;
        setBatchProgress(p || null);
        console.log('[AdminDashboard] batch:progress', p);
        if (p && p.status !== 'running') {
          stopBatchPolling();
          setBatchRunning(false);
          setLastBatchSummary(p.locations || []);
          const succeeded = (p.locations || [])
            .filter((row) => row.status === 'success')
            .map((row) => row.location)
            .filter(Boolean);
          if (succeeded.length) {
            setPredictionRecalcQueue((prev) => prev.filter((loc) => !succeeded.includes(loc)));
          }
          setOk(
            t('batchDone', {
              ok: p.total - p.failed,
              fail: p.failed,
              total: p.total,
            })
          );
          loadAllPreviewsRef.current?.();
        }
      } catch (e) {
        console.error('[AdminDashboard] batch:progress:error', e);
      }
    },
    [stopBatchPolling, token]
  );


  const t = useCallback((key, params = {}) => translate(adminUiLang, `adminThresholds.${key}`, params), [adminUiLang]);

  const canCall = useMemo(() => token.trim().length > 0, [token]);

  useEffect(() => {
    const saved = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (saved?.trim()) {
      setToken(saved.trim());
      setPendingToken(saved.trim());
    }
  }, []);

  useEffect(() => {
    if (!token.trim()) return;
    loadMatrix();
    loadAllThresholds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openAuthDialog = useCallback(() => {
    setPendingToken(token || sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || '');
    setAuthOpen(true);
  }, [token]);

  const applyAuth = useCallback(() => {
    const next = pendingToken.trim();
    if (!next) return;
    sessionStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, next);
    setToken(next);
    setAuthOpen(false);
    setError('');
  }, [pendingToken]);

  const logoutAuth = useCallback(() => {
    sessionStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
    setToken('');
    setPendingToken('');
    setLocations([]);
    setRows({});
    setMatrixData(null);
    setPreviewMeta(null);
    setDayPreviewByLoc({});
    setMonthPredictionsByLoc({});
  }, []);

  const requireAuth = useCallback(() => {
    if (token.trim()) return true;
    openAuthDialog();
    setError(t('needAuth'));
    return false;
  }, [token, openAuthDialog, t]);
  const monthDayOptions = useMemo(() => Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0')), []);
  const actionGroupOptions = useMemo(() => {
    const source = thresholdGroups.length
      ? thresholdGroups
      : ACTION_GROUP_OPTIONS.map((opt) => ({
          id: opt.value,
          label_ja: opt.labelJa,
          label_en: opt.labelEn,
          description_ja: '',
          description_en: '',
          min_label_ja: opt.minLabelJa,
          max_label_ja: opt.maxLabelJa,
          mid_label_ja: opt.midLabelJa,
          min_label_en: opt.minLabelEn,
          max_label_en: opt.maxLabelEn,
          mid_label_en: opt.midLabelEn,
          unit_note_ja: opt.unitNoteJa,
          unit_note_en: opt.unitNoteEn,
        }));
    return source.map((g) => mapActionGroupCatalogEntry(g, adminUiLang));
  }, [adminUiLang, thresholdGroups]);

  const currentActionGroupMeta = useMemo(
    () => actionGroupOptions.find((g) => g.value === actionGroup),
    [actionGroupOptions, actionGroup]
  );

  const thresholdFieldLabels = useMemo(() => {
    if (currentActionGroupMeta) {
      return {
        min: currentActionGroupMeta.minLabel,
        max: currentActionGroupMeta.maxLabel,
        mid: currentActionGroupMeta.midLabel,
        unitNote: currentActionGroupMeta.unitNote,
      };
    }
    return { min: t('minPeople'), max: t('maxPeople'), mid: t('midPeople'), unitNote: '' };
  }, [currentActionGroupMeta, t]);

  useEffect(() => {
    if (!canCall) return;
    api('/api/admin/thresholds/groups', { token })
      .then((res) => setThresholdGroups(res?.data || []))
      .catch(() => setThresholdGroups([]));
  }, [canCall, token]);
  const resolvedPreviewByLoc = useMemo(() => {
    if (previewUnit === 'day') return dayPreviewByLoc;
    const result = {};
    Object.entries(monthPredictionsByLoc).forEach(([loc, payload]) => {
      const predictions = Array.isArray(payload) ? payload : payload?.predictions || [];
      const { hourly, usedDate } = resolveMonthHourly(predictions, previewMonth, previewMonthDay);
      result[loc] = {
        hourly: hourly.map((p) => ({
          hour: p.hour,
          predicted_count: p.predicted_count,
          congestion_level: p.congestion_level,
        })),
        actualHourly: [],
        previewDateUsed: usedDate,
      };
    });
    return result;
  }, [previewUnit, dayPreviewByLoc, monthPredictionsByLoc, previewMonth, previewMonthDay]);

  useEffect(() => {
    if (!locations.length) {
      setPreviewStripLoc('');
      return;
    }
    setPreviewStripLoc((prev) => (prev && locations.includes(prev) ? prev : locations[0]));
  }, [locations]);

  const setRow = useCallback((loc, patch) => {
    setRows((prev) => ({
      ...prev,
      [loc]: { ...prev[loc], ...patch },
    }));
  }, []);

  const loadMatrix = async () => {
    if (!requireAuth()) return;
    setLoadingMatrix(true);
    try {
      const res = await api('/api/admin/thresholds/matrix', { token });
      setMatrixData(res?.data || null);
    } catch (e) {
      setError(String(e.message || e));
      setMatrixData(null);
    } finally {
      setLoadingMatrix(false);
    }
  };

  const loadAllThresholds = async () => {
    console.log('[AdminDashboard] loadAllThresholds:start');
    if (!requireAuth()) return;
    setError('');
    setOk('');
    setLoadingThresholds(true);
    try {
      const res = await api('/api/admin/thresholds/all', {
        token,
        params: { action_group: actionGroup },
      });
      const list = res?.data || [];
      const locs = [];
      const next = {};
      list.forEach((item) => {
        locs.push(item.location);
        const rule = item.rule;
        const builtin = item.builtin_default;
        const effective = item.effective_rule;
        const valueSource = rule || effective || builtin;
        next[item.location] = {
          min: valueSource ? String(valueSource.min_threshold) : '',
          mid:
            rule?.middle_threshold != null && rule?.middle_threshold !== undefined
              ? String(rule.middle_threshold)
              : '',
          max: valueSource ? String(valueSource.max_threshold) : '',
          savedRule: rule || null,
          effectiveRule: effective || null,
          builtinDefault: builtin || null,
        };
      });
      setLocations(locs);
      setRows(next);
      setOk(t('loadedOk', { n: locs.length }));
      console.log('[AdminDashboard] loadAllThresholds:success', { locations: locs.length });
    } catch (e) {
      setError(String(e.message || e));
      console.error('[AdminDashboard] loadAllThresholds:error', e);
    } finally {
      setLoadingThresholds(false);
    }
  };

  useEffect(() => {
    if (!canCall) return;
    loadMatrix();
    loadAllThresholds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionGroup, canCall]);

  const handleMatrixSelectLocation = useCallback((loc) => {
    setPreviewStripLoc(loc);
  }, []);

  const handleMatrixHighlightCell = useCallback((loc, groupId) => {
    setPreviewStripLoc(loc);
    if (groupId && groupId !== actionGroup) {
      setActionGroup(groupId);
    }
  }, [actionGroup]);

  const handleMatrixEditCell = useCallback((loc, groupId) => {
    setPreviewStripLoc(loc);
    if (groupId && groupId !== actionGroup) {
      setActionGroup(groupId);
    }
    setThresholdView('edit');
  }, [actionGroup]);

  const startSplitDrag = useCallback((clientX) => {
    splitDraggingRef.current = true;
    const updateFromClientX = (x) => {
      const el = splitContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const pct = ((x - rect.left) / rect.width) * 100;
      setSplitPct(Math.min(SPLIT_PCT_MAX, Math.max(SPLIT_PCT_MIN, pct)));
    };
    updateFromClientX(clientX);
    const onMove = (e) => {
      if (!splitDraggingRef.current) return;
      updateFromClientX(e.clientX);
    };
    const onUp = () => {
      if (!splitDraggingRef.current) return;
      splitDraggingRef.current = false;
      setSplitPct((prev) => {
        sessionStorage.setItem(SPLIT_PCT_STORAGE_KEY, String(prev));
        return prev;
      });
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const applyBuiltinToRow = (loc) => {
    const row = rows[loc];
    const b = row?.builtinDefault;
    if (!b) return;
    setRow(loc, {
      min: String(b.min_threshold),
      max: String(b.max_threshold),
      mid: '',
    });
  };

  const seedDefaults = async (overwrite = false) => {
    if (!requireAuth()) return;
    const groupLabel = currentActionGroupMeta?.label || actionGroup;
    const confirmMsg = overwrite
      ? t('seedConfirmOverwrite', { group: groupLabel })
      : t('seedConfirmMissing', { group: groupLabel });
    if (!window.confirm(confirmMsg)) return;
    setError('');
    setOk('');
    setSeedingDefaults(true);
    try {
      const res = await api('/api/admin/thresholds/seed-defaults', {
        method: 'POST',
        token,
        params: { action_group: actionGroup, overwrite: overwrite ? 1 : 0 },
      });
      const d = res?.data || {};
      await loadAllThresholds();
      await loadMatrix();
      setOk(
        t('seedOk', {
          created: d.created ?? 0,
          updated: d.updated ?? 0,
          skipped: d.skipped ?? 0,
        })
      );
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSeedingDefaults(false);
    }
  };

  const saveRow = async (loc) => {
    console.log('[AdminDashboard] saveRow:start', { loc, row: rows[loc] });
    if (!requireAuth()) return;
    setError('');
    setOk('');
    const row = rows[loc];
    if (!row) return;
    const min = toNumberOrNull(row.min);
    const max = toNumberOrNull(row.max);
    const mid = row.mid === '' ? undefined : toNumberOrNull(row.mid);
    if (min == null || max == null) {
      setError(t('errMinMaxNamed', { min: thresholdFieldLabels.min, max: thresholdFieldLabels.max }));
      return;
    }
    if (min >= max) {
      setError(t('errOrderNamed', { min: thresholdFieldLabels.min, max: thresholdFieldLabels.max }));
      return;
    }
    if (mid != null && !(min < mid && mid < max)) {
      setError(t('errMiddleNamed', { min: thresholdFieldLabels.min, max: thresholdFieldLabels.max, mid: thresholdFieldLabels.mid }));
      return;
    }
    try {
      await api('/api/admin/thresholds', {
        method: 'PUT',
        token,
        params: {
          location: loc,
          action_group: actionGroup,
          min_threshold: min,
          max_threshold: max,
          middle_threshold: mid,
          updated_by: 'admin-ui',
          change_note: batchNote || undefined,
        },
      });
      await loadAllThresholds();
      await loadMatrix();
      await loadAllPreviews();
      if (actionGroup === 'prediction') {
        setPredictionRecalcQueue((prev) => (prev.includes(loc) ? prev : [...prev, loc]));
      }
      setOk(t('savedOk', { place: placePrimary(loc, adminUiLang) }));
      console.log('[AdminDashboard] saveRow:success', { loc, min, mid, max });
    } catch (e) {
      setError(String(e.message || e));
      console.error('[AdminDashboard] saveRow:error', { loc, e });
    }
  };

  const deleteRow = async (loc) => {
    console.log('[AdminDashboard] deleteRow:start', { loc, actionGroup });
    if (!requireAuth()) return;
    const confirmMsg =
      adminUiLang === 'en'
        ? `Delete threshold rule for ${placePrimary(loc, adminUiLang)} (${actionGroup})? The system will fall back to built-in defaults.`
        : `${placePrimary(loc, adminUiLang)}（${actionGroup}）の閾値ルールを削除しますか？\nシステムはコード内ハードコード値にフォールバックします。`;
    if (!window.confirm(confirmMsg)) return;
    setError('');
    setOk('');
    try {
      await api('/api/admin/thresholds', {
        method: 'DELETE',
        token,
        params: { location: loc, action_group: actionGroup },
      });
      await loadAllThresholds();
      await loadMatrix();
      setOk(
        adminUiLang === 'en'
          ? `Deleted rule for ${placePrimary(loc, adminUiLang)} (${actionGroup})`
          : `${placePrimary(loc, adminUiLang)}（${actionGroup}）のルールを削除しました`
      );
      console.log('[AdminDashboard] deleteRow:success', { loc, actionGroup });
    } catch (e) {
      setError(String(e.message || e));
      console.error('[AdminDashboard] deleteRow:error', { loc, e });
    }
  };

  const loadAllPreviews = useCallback(async () => {
    const config = getPreviewConfig(actionGroup);
    console.log('[AdminDashboard] loadAllPreviews:start', {
      actionGroup,
      previewKind: config.kind,
      previewUnit,
      previewDate,
      previewMonth,
      locations: locations.length,
    });
    if (!token.trim() || !locations.length) return;
    setLoadingPreview(true);
    let okc = 0;
    let fail = 0;
    const next = {};
    const [year, monthNum] = previewMonth.split('-').map((v) => parseInt(v, 10));

    await Promise.all(
      locations.map(async (loc) => {
        try {
          if (config.kind === 'prediction') {
            if (previewUnit === 'day') {
              const res = await api('/api/prediction/congestion/daily', {
                params: { location: loc, date: previewDate, nocache: 1 },
              });
              next[loc] = {
                hourly: res?.data?.hourly || [],
                actualHourly: res?.data?.actual_hourly || [],
              };
            } else {
              const res = await api('/api/prediction/congestion', {
                params: { location: loc, year, month: monthNum, nocache: 1 },
              });
              next[loc] = {
                predictions: res?.data?.predictions || [],
                dailySummary: res?.data?.daily_summary || [],
              };
            }
          } else {
            const body = {
              place: loc,
              action: config.graphAction,
              year,
              month: monthNum,
            };
            if (config.kind === 'event_effect') {
              const dayRaw =
                previewUnit === 'day' ? previewDate.split('-')[2] : previewMonthDay;
              const day = parseInt(dayRaw, 10);
              if (Number.isFinite(day)) body.day = day;
            }
            const res = await apiPost('/api/get-graph', body);
            next[loc] = {
              graphAction: config.graphAction,
              dashboardData: graphResponseToDashboardState(res, config.graphAction),
            };
          }
          okc += 1;
        } catch (err) {
          console.error('[AdminDashboard] loadAllPreviews:locError', { loc, err });
          if (config.kind === 'prediction') {
            next[loc] =
              previewUnit === 'day'
                ? { hourly: [], actualHourly: [] }
                : { predictions: [], dailySummary: [] };
          } else {
            next[loc] = {
              graphAction: config.graphAction,
              dashboardData: graphResponseToDashboardState(null, config.graphAction),
            };
          }
          fail += 1;
        }
      })
    );

    if (config.kind === 'prediction') {
      if (previewUnit === 'day') {
        setDayPreviewByLoc(next);
        setMonthPredictionsByLoc({});
      } else {
        setMonthPredictionsByLoc(next);
        setDayPreviewByLoc({});
      }
      setProductionPreviewByLoc({});
    } else {
      setProductionPreviewByLoc(next);
      setDayPreviewByLoc({});
      setMonthPredictionsByLoc({});
    }
    setPreviewMeta({ ok: okc, fail });
    setLoadingPreview(false);
    console.log('[AdminDashboard] loadAllPreviews:done', { ok: okc, fail });
  }, [token, locations, actionGroup, previewUnit, previewDate, previewMonth, previewMonthDay]);

  loadAllPreviewsRef.current = loadAllPreviews;

  useEffect(() => {
    if (!canCall || !locations.length) return undefined;
    const timer = setTimeout(() => {
      loadAllPreviews();
    }, 400);
    return () => clearTimeout(timer);
  }, [canCall, locations, actionGroup, previewUnit, previewDate, previewMonth, previewMonthDay, loadAllPreviews]);

  const resolvePredictionBatchTargets = useCallback(() => {
    const targets = new Set(predictionRecalcQueue);
    if (actionGroup === 'prediction') {
      locations.forEach((loc) => {
        const row = rows[loc];
        if (!row) return;
        const dirty =
          row.savedRule &&
          (String(row.savedRule.min_threshold) !== String(row.min).trim() ||
            String(row.savedRule.max_threshold) !== String(row.max).trim() ||
            (row.mid !== '' &&
              String(row.savedRule.middle_threshold ?? '') !== String(row.mid).trim()));
        if (dirty) targets.add(loc);
      });
    }
    return [...targets];
  }, [predictionRecalcQueue, actionGroup, locations, rows]);

  const runPredictionBatch = async (targetLocations, { runAll = false } = {}) => {
    console.log('[AdminDashboard] runPredictionBatch:start', { targetLocations, runAll });
    if (!requireAuth()) return;
    setError('');
    setOk('');
    setBatchRunning(true);
    setBatchProgress(null);
    stopBatchPolling();
    try {
      const params = runAll ? {} : { locations: targetLocations.join(',') };
      const res = await api('/api/admin/prediction/run', { method: 'POST', token, params });
      const batchId = res?.data?.batch_id;
      if (!batchId) {
        throw new Error('batch_id was not returned');
      }
      setOk(t('batchStarted', { n: targetLocations.length, batchId }));
      await fetchBatchProgress(batchId);
      batchPollTimerRef.current = setInterval(() => {
        fetchBatchProgress(batchId);
      }, 1500);
      console.log('[AdminDashboard] runPredictionBatch:started', { batchId, targetLocations });
    } catch (e) {
      setError(String(e.message || e));
      console.error('[AdminDashboard] runPredictionBatch:error', e);
      stopBatchPolling();
      setBatchRunning(false);
    }
  };

  const runBatchForChanged = async () => {
    if (!requireAuth()) return;
    const targets = resolvePredictionBatchTargets();
    if (!targets.length) {
      setError(t('batchNoTargets'));
      return;
    }
    const labels = targets.map((loc) => placePrimary(loc, adminUiLang)).join('、');
    if (!window.confirm(t('batchConfirmChanged', { n: targets.length, places: labels }))) return;
    await runPredictionBatch(targets);
  };

  const runBatchAllLocations = async () => {
    if (!requireAuth()) return;
    if (!window.confirm(t('batchConfirmAll'))) return;
    await runPredictionBatch(locations.length ? locations : [], { runAll: true });
  };

  const predictionBatchTargetCount = resolvePredictionBatchTargets().length;

  useEffect(() => () => stopBatchPolling(), [stopBatchPolling]);

  const openHistory = async (loc) => {
    console.log('[AdminDashboard] openHistory:start', { loc });
    if (!requireAuth()) return;
    setHistoryLoc(loc);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const res = await api('/api/admin/thresholds/history', {
        token,
        params: { location: loc, action_group: actionGroup, limit: 25 },
      });
      setHistoryRows(res?.data || []);
      console.log('[AdminDashboard] openHistory:success', { loc, rows: (res?.data || []).length });
    } catch (e) {
      setHistoryRows([]);
      setError(String(e.message || e));
      console.error('[AdminDashboard] openHistory:error', { loc, e });
    } finally {
      setHistoryLoading(false);
    }
  };

  const applyHistoryItem = (item) => {
    console.log('[AdminDashboard] applyHistoryItem', { historyLoc, item });
    if (!historyLoc) return;
    setRow(historyLoc, {
      min: String(item.min_threshold ?? ''),
      mid: item.middle_threshold == null ? '' : String(item.middle_threshold),
      max: String(item.max_threshold ?? ''),
    });
    setOk(
      t('historyApplied', {
        id: item.id,
        place: placePrimary(historyLoc, adminUiLang),
      })
    );
  };

  const draftMinMax = useCallback(
    (loc) => {
      const row = rows[loc];
      if (!row) return { draftMin: null, draftMax: null };
      let draftMin = toNumberOrNull(row.min);
      let draftMax = toNumberOrNull(row.max);
      if (draftMin == null || draftMax == null) {
        const src = row.savedRule || row.effectiveRule || row.builtinDefault;
        if (src) {
          draftMin = draftMin ?? src.min_threshold;
          draftMax = draftMax ?? src.max_threshold;
        }
      }
      return { draftMin, draftMax };
    },
    [rows]
  );

  const previewFetched = previewMeta != null;

  const predictionBoundsForLoc = useCallback(
    (loc) => {
      const row = matrixData?.rows?.find((r) => r.location === loc);
      const cell = row?.cells?.prediction;
      const src = cell?.rule || cell?.builtin_default;
      if (src) {
        return { draftMin: src.min_threshold, draftMax: src.max_threshold };
      }
      return { draftMin: null, draftMax: null };
    },
    [matrixData]
  );

  const previewDraftBounds = useCallback(
    (loc) => {
      const edited = draftMinMax(loc);
      if (edited.draftMin != null && edited.draftMax != null) return edited;
      const row = rows[loc];
      const src = row?.savedRule || row?.effectiveRule || row?.builtinDefault;
      if (src) {
        return { draftMin: src.min_threshold, draftMax: src.max_threshold };
      }
      const matrixRow = matrixData?.rows?.find((r) => r.location === loc);
      const cell = matrixRow?.cells?.[actionGroup];
      const matrixSrc = cell?.rule || cell?.builtin_default;
      if (matrixSrc) {
        return { draftMin: matrixSrc.min_threshold, draftMax: matrixSrc.max_threshold };
      }
      return { draftMin: null, draftMax: null };
    },
    [actionGroup, draftMinMax, rows, matrixData]
  );

  const productionBoundsForStrip = useCallback(
    (loc) => {
      const row = rows[loc];
      const src = row?.savedRule || row?.effectiveRule || row?.builtinDefault;
      if (src) {
        return { draftMin: src.min_threshold, draftMax: src.max_threshold };
      }
      return predictionBoundsForLoc(loc);
    },
    [rows, predictionBoundsForLoc]
  );

  const stripProduction = previewStripLoc ? productionBoundsForStrip(previewStripLoc) : { draftMin: null, draftMax: null };
  const stripDraft = previewStripLoc ? previewDraftBounds(previewStripLoc) : { draftMin: null, draftMax: null };
  const showThresholdCompare =
    Boolean(previewStripLoc) && boundsDiffer(stripProduction, stripDraft);

  const previewMonthParts = useMemo(() => {
    const [y, m] = previewMonth.split('-').map((v) => parseInt(v, 10));
    return { year: y, month: m };
  }, [previewMonth]);

  const productionPreviewTitle = useMemo(() => {
    const label = currentActionGroupMeta?.label || actionGroup;
    return t('previewProductionTitle', { group: label });
  }, [currentActionGroupMeta, actionGroup, t]);

  const effectiveSplitPct = previewLayout === 'focus' ? Math.min(splitPct, 45) : splitPct;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box', px: 0.5, py: 0.25, bgcolor: '#e8eaed' }}>
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', py: 0.25, px: 0.5, borderBottom: '1px solid #cfd8dc', bgcolor: '#fff' }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {t('title')}
        </Typography>
        <Button size="small" href="/purpose" sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: 11 }}>
          {t('back')}
        </Button>

        <ToggleButtonGroup
          size="small"
          value={adminUiLang}
          exclusive
          onChange={(_, v) => v && setAdminUiLang(v)}
          sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 0.75, fontSize: 11 } }}
        >
          <ToggleButton value="ja">{t('uiJa')}</ToggleButton>
          <ToggleButton value="en">{t('uiEn')}</ToggleButton>
        </ToggleButtonGroup>

        <FormControlLabel
          control={<Checkbox size="small" checked={showPlaceSlug} onChange={(e) => setShowPlaceSlug(e.target.checked)} />}
          label={<Typography variant="caption">{t('showSlug')}</Typography>}
          sx={{ mr: 0.5, ml: 0 }}
        />

        <ToggleButtonGroup
          size="small"
          value={previewLayout}
          exclusive
          onChange={(_, v) => v && setPreviewLayout(v)}
          sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 0.75, fontSize: 11 } }}
        >
          <ToggleButton value="overview">{t('layoutOverview')}</ToggleButton>
          <ToggleButton value="focus">{t('layoutFocus')}</ToggleButton>
        </ToggleButtonGroup>

        <Box component="form" autoComplete="off" onSubmit={(e) => e.preventDefault()} sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {!canCall ? (
            <Button size="small" variant="contained" onClick={openAuthDialog} sx={{ minWidth: 0, px: 1 }}>
              {t('loginBtn')}
            </Button>
          ) : (
            <>
              <Chip label={t('authed')} size="small" color="success" sx={{ height: 22, fontSize: 10 }} />
              <Button
                size="small"
                variant="outlined"
                disabled={loadingThresholds || loadingMatrix}
                onClick={() => {
                  loadMatrix();
                  loadAllThresholds();
                }}
                sx={{ minWidth: 0, px: 1 }}
              >
                {t('reloadBtn')}
              </Button>
              <Button size="small" variant="text" onClick={logoutAuth} sx={{ minWidth: 0, px: 0.75, fontSize: 11 }}>
                {t('logoutBtn')}
              </Button>
            </>
          )}
          <Button
            size="small"
            variant="outlined"
            disabled={!canCall || seedingDefaults}
            onClick={() => seedDefaults(false)}
            title={t('seedBtnHelp')}
            sx={{ minWidth: 0, px: 1 }}
          >
            {t('seedBtn')}
          </Button>
          <FormControl
            size="small"
            sx={{
              minWidth: 190,
              ...TF_DENSE,
              ...(thresholdView === 'edit'
                ? {
                    '& .MuiOutlinedInput-root': { bgcolor: '#e3f2fd', borderColor: '#1565c0' },
                    '& .MuiInputLabel-root': { fontWeight: 700, color: '#1565c0' },
                  }
                : {}),
            }}
          >
            <InputLabel id="action-group-label">{t('actionGroupLabel')}</InputLabel>
            <Select labelId="action-group-label" value={actionGroup} label={t('actionGroupLabel')} onChange={(e) => setActionGroup(e.target.value)}>
              {actionGroupOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box>
                    <Typography component="span" sx={{ fontSize: 13 }}>
                      {opt.label}
                    </Typography>
                    {opt.unitNote && (
                      <Typography component="span" sx={{ display: 'block', fontSize: 10, color: 'text.secondary', lineHeight: 1.2 }}>
                        {opt.unitNote}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {previewUnit === 'day' ? (
            <TextField size="small" type="date" label={t('dateLabel')} value={previewDate} onChange={(e) => setPreviewDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150, ...TF_DENSE }} />
          ) : (
            <>
              <TextField size="small" type="month" label={t('monthLabel')} value={previewMonth} onChange={(e) => setPreviewMonth(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160, ...TF_DENSE }} />
              <FormControl size="small" sx={{ minWidth: 90, ...TF_DENSE }}>
                <InputLabel id="preview-day-label">{t('monthDayLabel')}</InputLabel>
                <Select labelId="preview-day-label" value={previewMonthDay} label={t('monthDayLabel')} onChange={(e) => setPreviewMonthDay(e.target.value)}>
                  {monthDayOptions.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={batchRunning || !canCall || predictionBatchTargetCount === 0}
            onClick={runBatchForChanged}
            title={t('batchBtnHelp')}
            sx={{ minWidth: 0, px: 1 }}
          >
            {batchRunning
              ? t('batchRunning')
              : t('batchBtn', { n: predictionBatchTargetCount })}
          </Button>
          <Button
            size="small"
            variant="text"
            disabled={batchRunning || !canCall}
            onClick={runBatchAllLocations}
            title={t('batchAllBtnHelp')}
            sx={{ minWidth: 0, px: 0.75, fontSize: 10 }}
          >
            {t('batchAllBtn')}
          </Button>
          {loadingPreview && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              {t('previewLoading')}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 220px', lineHeight: 1.25 }}>
          {currentActionGroupMeta?.description || t('hintShort')}
        </Typography>
      </Box>

      {actionGroup === 'default' && (
        <Alert severity="warning" sx={{ py: 0, px: 1, flexShrink: 0, '& .MuiAlert-message': { py: 0.25 } }}>
          <Typography variant="caption">
            {adminUiLang === 'en'
              ? '⚠ You are editing the "default (fallback)" group. These values apply to ALL views (calendar, heatmap, etc.) that do not have their own group-specific rule. Changes here affect congestion display site-wide.'
              : '⚠ 「共通（フォールバック）」グループを編集中です。このグループの値は、専用ルールが設定されていない全ビュー（カレンダー・ヒートマップ等）に適用されます。誤った値を設定するとサイト全体の混雑度表示に影響します。'}
          </Typography>
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ py: 0, px: 1, flexShrink: 0, '& .MuiAlert-message': { py: 0.25 } }} onClose={() => setError('')}>
          <Typography variant="caption">{error}</Typography>
        </Alert>
      )}
      {ok && (
        <Alert severity="success" sx={{ py: 0, px: 1, flexShrink: 0, '& .MuiAlert-message': { py: 0.25 } }} onClose={() => setOk('')}>
          <Typography variant="caption">{ok}</Typography>
        </Alert>
      )}
      {lastBatchSummary && (
        <Alert severity="info" sx={{ py: 0, px: 1, flexShrink: 0, '& pre': { m: 0, fontSize: 10, maxHeight: 72, overflow: 'auto' } }} onClose={() => setLastBatchSummary(null)}>
          <Typography variant="caption" component="pre">
            {JSON.stringify(lastBatchSummary, null, 2)}
          </Typography>
        </Alert>
      )}
      {batchRunning && (
        <Alert severity="info" sx={{ py: 0, px: 1, flexShrink: 0 }}>
          <Typography variant="caption">
            予測バッチを実行中です…
            {batchProgress ? ` ${batchProgress.done}/${batchProgress.total} 完了（${batchProgress.progress_percent}%）` : ''}
          </Typography>
        </Alert>
      )}

      <Box ref={splitContainerRef} sx={{ flex: 1, display: 'flex', minHeight: 0, pt: 0.5, alignItems: 'stretch' }}>
        <Paper
          elevation={0}
          sx={{
            width: `${effectiveSplitPct}%`,
            flexShrink: 0,
            minWidth: 240,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: '1px solid #b0bec5',
            borderRadius: 0,
          }}
        >
          <Box sx={{ px: 0.5, py: 0.25, bgcolor: '#cfd8dc', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" fontWeight={700}>
              {thresholdView === 'matrix' ? t('matrixTitle') : t('editPanelTitle')}
              {thresholdView === 'edit' && ` · ${locations.length}${t('placesUnit')}`}
            </Typography>
            {thresholdView === 'edit' && currentActionGroupMeta && (
              <Chip
                label={currentActionGroupMeta.label}
                size="small"
                color="primary"
                sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
              />
            )}
            <ToggleButtonGroup
              size="small"
              value={thresholdView}
              exclusive
              onChange={(_, v) => v && setThresholdView(v)}
              sx={{ '& .MuiToggleButton-root': { py: 0.1, px: 0.6, fontSize: 10 } }}
            >
              <ToggleButton value="matrix">{t('viewMatrix')}</ToggleButton>
              <ToggleButton value="edit">{t('viewEdit')}</ToggleButton>
            </ToggleButtonGroup>
            {thresholdView === 'matrix' && (
              <Button size="small" variant="text" disabled={loadingMatrix} onClick={loadMatrix} sx={{ minWidth: 0, fontSize: 10 }}>
                {t('reloadMatrix')}
              </Button>
            )}
          </Box>
          {thresholdView === 'matrix' ? (
            <Box sx={{ flex: 1, overflow: 'auto', p: 0.35 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: 10 }}>
                {t('matrixHint')}
              </Typography>
              <ThresholdMatrixPanel
                matrixData={matrixData}
                selectedLocation={previewStripLoc}
                selectedGroup={actionGroup}
                onSelectLocation={handleMatrixSelectLocation}
                onHighlightCell={handleMatrixHighlightCell}
                onEditCell={handleMatrixEditCell}
                adminUiLang={adminUiLang}
                showPlaceSlug={showPlaceSlug}
                t={t}
              />
            </Box>
          ) : (
          <>
            <Alert
              severity="info"
              icon={false}
              sx={{ py: 0.25, px: 0.75, flexShrink: 0, bgcolor: '#e3f2fd', '& .MuiAlert-message': { width: '100%', py: 0.25 } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>
                  {t('editPanelTitle')}
                </Typography>
                <Chip label={currentActionGroupMeta?.label || actionGroup} color="primary" size="small" sx={{ height: 22, fontWeight: 700 }} />
                {thresholdFieldLabels.unitNote && (
                  <Chip label={thresholdFieldLabels.unitNote} size="small" variant="outlined" sx={{ height: 22, fontSize: 10 }} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  {currentActionGroupMeta?.description || t('editPanelOpenGroup')}
                </Typography>
              </Box>
            </Alert>
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#eceff1', py: 0.25, px: 0.5, fontSize: 11 }}>{t('placeCol')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#eceff1', py: 0.25, px: 0.5, fontSize: 11, minWidth: 108 }}>{thresholdFieldLabels.min}</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#eceff1', py: 0.25, px: 0.5, fontSize: 11, minWidth: 108 }}>{thresholdFieldLabels.mid}</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#eceff1', py: 0.25, px: 0.5, fontSize: 11, minWidth: 108 }}>{thresholdFieldLabels.max}</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#eceff1', py: 0.25, px: 0.5, fontSize: 11 }}>{t('dbCol')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#eceff1', py: 0.25, px: 0.25, fontSize: 11, width: 52 }} />
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#eceff1', py: 0.25, px: 0.25, fontSize: 11, width: 44 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ px: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('emptyHint')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {locations.map((loc) => {
                  const row = rows[loc] || { min: '', mid: '', max: '', savedRule: null, effectiveRule: null, builtinDefault: null };
                  const saved = row.savedRule;
                  const builtin = row.builtinDefault;
                  const dbLabel = saved ? `${saved.updated_at?.slice(5, 16) || ''}` : '—';
                  const dirty =
                    saved &&
                    (String(saved.min_threshold) !== String(row.min).trim() ||
                      String(saved.max_threshold) !== String(row.max).trim());
                  const sel = previewStripLoc === loc;
                  const primary = placePrimary(loc, adminUiLang);
                  const minVal = toNumberOrNull(row.min);
                  const maxVal = toNumberOrNull(row.max);
                  // min が max の 50% を超えると下位ビン (3-10) が極端に狭くなり二値分布が起きやすい
                  const rangeWarn =
                    minVal != null && maxVal != null && maxVal > 0 && minVal / maxVal > 0.5;
                  const rangeWarnMsg =
                    adminUiLang === 'en'
                      ? `min is more than 50% of max (${minVal}/${maxVal}). This may cause congestion levels to cluster at 1 or 11 when average counts are below min.`
                      : `min が max の 50% を超えています（${minVal}/${maxVal}）。平均カウントが min を下回ると混雑度が 1 か 11 に偏る可能性があります。`;
                  return (
                    <TableRow key={loc} hover selected={sel}>
                      <TableCell
                        sx={{ py: 0.15, px: 0.5, cursor: 'pointer', borderLeft: sel ? '3px solid #1565c0' : 'none', maxWidth: 140 }}
                        onClick={() => setPreviewStripLoc(loc)}
                        title={placeTooltip(loc)}
                      >
                        <Typography sx={{ fontSize: 11, lineHeight: 1.25, fontWeight: 600 }}>{primary}</Typography>
                        {showPlaceSlug && (
                          <Typography sx={{ fontSize: 9, fontFamily: 'monospace', color: 'text.secondary', lineHeight: 1.2 }}>{loc}</Typography>
                        )}
                        {dirty && (
                          <Chip label={t('unsaved')} size="small" color="warning" sx={{ height: 16, fontSize: 8, mt: 0.25 }} />
                        )}
                        {!saved && (
                          <Chip label={t('builtinShort')} size="small" variant="outlined" sx={{ height: 16, fontSize: 8, mt: 0.25 }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 0.15, px: 0.35 }}>
                        <TextField
                          size="small"
                          value={row.min}
                          onChange={(e) => setRow(loc, { min: e.target.value })}
                          margin="none"
                          error={rangeWarn}
                          placeholder={thresholdFieldLabels.min}
                          sx={{ minWidth: 100, ...TF_DENSE }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.15, px: 0.35 }}>
                        <TextField
                          size="small"
                          value={row.mid}
                          onChange={(e) => setRow(loc, { mid: e.target.value })}
                          margin="none"
                          placeholder={thresholdFieldLabels.mid}
                          sx={{ minWidth: 100, ...TF_DENSE }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.15, px: 0.35 }}>
                        <TextField
                          size="small"
                          value={row.max}
                          onChange={(e) => setRow(loc, { max: e.target.value })}
                          margin="none"
                          error={rangeWarn}
                          placeholder={thresholdFieldLabels.max}
                          sx={{ minWidth: 100, ...TF_DENSE }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.15, px: 0.35, maxWidth: 72 }}>
                        <Typography variant="caption" color="text.secondary" noWrap title={saved ? JSON.stringify(saved) : ''} sx={{ fontSize: 10 }}>
                          {dbLabel}
                        </Typography>
                        {rangeWarn && (
                          <Typography variant="caption" color="warning.main" title={rangeWarnMsg} sx={{ display: 'block', fontSize: 9, cursor: 'help' }}>
                            ⚠ min/max 比
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 0.15, px: 0.25 }}>
                        <Button size="small" variant="contained" disabled={!canCall} onClick={() => saveRow(loc)} sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 11 }}>
                          {t('save')}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ py: 0.15, px: 0.25 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Button size="small" variant="text" disabled={!canCall} onClick={() => openHistory(loc)} sx={{ minWidth: 0, px: 0.25, fontSize: 11 }}>
                            {t('history')}
                          </Button>
                          {!saved && builtin && (
                            <Button
                              size="small"
                              variant="text"
                              disabled={!canCall}
                              onClick={() => applyBuiltinToRow(loc)}
                              sx={{ minWidth: 0, px: 0.25, fontSize: 10 }}
                            >
                              {t('applyBuiltin')}
                            </Button>
                          )}
                          {saved && (
                            <Button
                              size="small"
                              variant="text"
                              color="error"
                              disabled={!canCall}
                              onClick={() => deleteRow(loc)}
                              title={adminUiLang === 'en' ? 'Delete rule (revert to built-in defaults)' : 'ルール削除（コード内デフォルトに戻す）'}
                              sx={{ minWidth: 0, px: 0.25, fontSize: 10 }}
                            >
                              {adminUiLang === 'en' ? 'Reset' : 'リセット'}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          </>
          )}
          <Box sx={{ px: 0.5, py: 0.25, borderTop: '1px solid #b0bec5', flexShrink: 0, bgcolor: '#fafafa' }}>
            <TextField size="small" label={t('memoLabel')} value={batchNote} onChange={(e) => setBatchNote(e.target.value)} fullWidth sx={TF_DENSE} />
          </Box>
        </Paper>

        <PanelSplitDivider onDrag={startSplitDrag} />

        <Paper elevation={0} sx={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #b0bec5', borderRadius: 0 }}>
          <Alert severity="info" sx={{ py: 0, px: 0.75, fontSize: 10, flexShrink: 0, '& .MuiAlert-message': { py: 0.25 } }}>
            {isPredictionPreview ? t('previewUsesPrediction') : t('previewUsesProduction')}
          </Alert>
          <Box sx={{ px: 0.5, py: 0.25, bgcolor: '#cfd8dc', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
              {t('splitDragHint')}
            </Typography>
            {previewLayout === 'overview' && isPredictionPreview && (
              <Typography variant="caption" fontWeight={700}>
                {t('miniLineTitle')}
                {previewMeta != null && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontSize: 10 }}>
                    ({t('previewStatus', { ok: previewMeta.ok, fail: previewMeta.fail })})
                  </Typography>
                )}
              </Typography>
            )}
            {previewLayout === 'focus' && (
              <Typography variant="caption" fontWeight={700}>
                {t('layoutFocus')} — {t('previewPlace')}
              </Typography>
            )}
            <FormControl size="small" sx={{ minWidth: 200, ...TF_DENSE }}>
              <InputLabel id="strip-loc-label">{t('previewPlace')}</InputLabel>
              <Select labelId="strip-loc-label" label={t('previewPlace')} value={previewStripLoc || ''} onChange={(e) => setPreviewStripLoc(e.target.value)}>
                {locations.map((loc) => (
                  <MenuItem key={loc} value={loc} dense sx={{ py: 0.5 }}>
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: 12 }}>
                        {placePrimary(loc, adminUiLang)}
                      </Typography>
                      {showPlaceSlug && (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 10, color: 'text.secondary' }}>
                          {loc}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {previewLayout === 'overview' && !previewFetched && locations.length > 0 && (
            <Alert severity="info" sx={{ py: 0, px: 0.75, fontSize: 11, flexShrink: 0 }}>
              {t('previewNotFetched')}
            </Alert>
          )}
          {previewLayout === 'overview' && isPredictionPreview && (
            <Box sx={{ flex: '0 0 auto', maxHeight: '34vh', overflow: 'auto', p: 0.35, bgcolor: '#fafafa', borderBottom: '1px solid #b0bec5' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.35 }}>
                {locations.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
                    {t('emptyFetchHint')}
                  </Typography>
                ) : (
                  locations.map((loc) => {
                    const prod = productionBoundsForStrip(loc);
                    const draft = previewDraftBounds(loc);
                    const locCompare = boundsDiffer(prod, draft);
                    return (
                      <MiniLevelPreview
                        key={loc}
                        locKey={loc}
                        primaryLabel={placePrimary(loc, adminUiLang)}
                        showSlug={showPlaceSlug}
                        hourly={previewFetched ? resolvedPreviewByLoc[loc]?.hourly || [] : []}
                        actualHourly={previewFetched ? resolvedPreviewByLoc[loc]?.actualHourly || [] : []}
                        preferActualForApi={useActualForPastDay}
                        productionMin={prod.draftMin}
                        productionMax={prod.draftMax}
                        draftMin={draft.draftMin}
                        draftMax={draft.draftMax}
                        showThresholdCompare={locCompare}
                        previewFetched={previewFetched}
                        t={t}
                      />
                    );
                  })
                )}
              </Box>
            </Box>
          )}
          {previewLayout === 'overview' && !isPredictionPreview && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, py: 0.35, display: 'block', bgcolor: '#fafafa', borderBottom: '1px solid #b0bec5' }}>
              {t('previewOverviewProductionHint')}
            </Typography>
          )}

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 0.5, bgcolor: '#fff' }}>
            {previewStripLoc && locations.includes(previewStripLoc) ? (
              <>
                {!previewFetched && (
                  <Alert severity="info" sx={{ py: 0.25, mb: 0.5, fontSize: 11 }}>
                    {t('previewNotFetched')}
                  </Alert>
                )}
                {isPredictionPreview ? (
                  <>
                    {resolvedPreviewByLoc[previewStripLoc]?.previewDateUsed && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {t('previewDateFallback', { date: resolvedPreviewByLoc[previewStripLoc].previewDateUsed })}
                      </Typography>
                    )}
                    <DashboardStylePreview
                      locKey={previewStripLoc}
                      previewData={resolvedPreviewByLoc[previewStripLoc] || { hourly: [], actualHourly: [] }}
                      productionMin={stripProduction.draftMin}
                      productionMax={stripProduction.draftMax}
                      draftMin={stripDraft.draftMin}
                      draftMax={stripDraft.draftMax}
                      showThresholdCompare={showThresholdCompare}
                      t={t}
                      uiLang={adminUiLang}
                      layoutMode={previewLayout}
                      showSlug={showPlaceSlug}
                      preferActualForApi={useActualForPastDay}
                    />
                  </>
                ) : (
                  <AdminProductionPreview
                    actionGroup={actionGroup}
                    location={previewStripLoc}
                    graphAction={productionPreviewByLoc[previewStripLoc]?.graphAction || previewConfig.graphAction}
                    dashboardData={productionPreviewByLoc[previewStripLoc]?.dashboardData}
                    year={previewMonthParts.year}
                    month={previewMonthParts.month}
                    eventDay={
                      previewConfig.kind === 'event_effect'
                        ? previewUnit === 'day'
                          ? parseInt(previewDate.split('-')[2], 10)
                          : parseInt(previewMonthDay, 10)
                        : ''
                    }
                    title={productionPreviewTitle}
                  />
                )}
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('emptyDetailHint')}
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog open={Boolean(historyLoc)} onClose={() => setHistoryLoc(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 0.75, fontSize: 14 }}>
          {t('historyTitle')} — {historyLoc ? placePrimary(historyLoc, adminUiLang) : ''}
          {showPlaceSlug && historyLoc && (
            <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
              {' '}
              ({historyLoc})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 0.75 }}>
          {historyLoading ? (
            <Typography variant="caption">…</Typography>
          ) : (
            <Stack spacing={0.75}>
              {!historyRows.length && <Typography variant="caption">{t('noneHistory')}</Typography>}
              {historyRows.map((item) => (
                <Paper key={item.id} variant="outlined" sx={{ p: 0.75 }}>
                  <Typography variant="caption" display="block">
                    #{item.id} {item.changed_at || ''} {item.changed_by || ''}
                  </Typography>
                  <Typography variant="caption">
                    min={item.min_threshold} mid={item.middle_threshold ?? '-'} max={item.max_threshold}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {item.change_note || ''}
                  </Typography>
                  <Button size="small" sx={{ mt: 0.25, py: 0, fontSize: 11 }} onClick={() => applyHistoryItem(item)}>
                    {t('apply')}
                  </Button>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 0.5 }}>
          <Button size="small" onClick={() => setHistoryLoc(null)}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={authOpen} onClose={() => setAuthOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('loginBtn')}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={(e) => e.preventDefault()}>
          {/* Chrome: password フィールドに username を添える */}
          <TextField
            name="username"
            type="text"
            autoComplete="username"
            defaultValue="admin-ui"
            tabIndex={-1}
            sx={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none', overflow: 'hidden' }}
            inputProps={{ autoComplete: 'username', tabIndex: -1 }}
          />
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            type="password"
            label={t('secretLabel')}
            value={pendingToken}
            onChange={(e) => setPendingToken(e.target.value)}
            autoComplete="current-password"
            inputProps={{ autoComplete: 'current-password' }}
          />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuthOpen(false)}>{t('close')}</Button>
          <Button variant="contained" onClick={applyAuth} disabled={!pendingToken.trim()}>
            {t('loginApply')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
